import Phaser from "phaser";
import { characterTexture, UI_TEXTURES } from "../assets";
import type { Board, Piece } from "../model/board";
import { motionEnabled } from "./preferences";
import { COLORS } from "./palette";
import { stageX, stageY } from "./stage";

export type Icon = "chat" | "speaker" | "muted" | "reset" | "check" | "close" | "exit" | "speed" | "trophy" | "gift" | "team" | "arrow";
const INK = 0xffefd0;

// Familiar shapes carry the instructions. Words remain in the screen-reader controls.
export function icon(scene: Phaser.Scene, kind: Icon, size = 48): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics().setScale(size / 64);
  g.fillStyle(INK).lineStyle(4, INK);
  if (kind === "speaker" || kind === "muted") {
    g.fillPoints([new Phaser.Math.Vector2(-22,-9), new Phaser.Math.Vector2(-13,-9), new Phaser.Math.Vector2(1,-21),
      new Phaser.Math.Vector2(1,21),new Phaser.Math.Vector2(-13,9),new Phaser.Math.Vector2(-22,9)], true);
    if (kind === "muted") g.lineBetween(12,-12,28,12).lineBetween(12,12,28,-12);
    else {
      g.beginPath().arc(1,0,14,-Math.PI/3,Math.PI/3).strokePath();
      g.beginPath().arc(1,0,25,-Math.PI/3,Math.PI/3).strokePath();
    }
  } else if (kind === "chat") {
    g.strokeRoundedRect(-24, -20, 48, 33, 9);
    g.lineBetween(-10, 13, -16, 24).lineBetween(-16, 24, 1, 13);
    for (const x of [-12, 0, 12]) g.fillCircle(x, -4, 2.5);
  } else if (kind === "reset") {
    g.lineStyle(4.5,INK).beginPath().arc(0,0,18,0,Math.PI*1.5).strokePath();
    g.fillTriangle(12,-18,-3,-27,-3,-9);
  } else if (kind === "check") g.lineStyle(6,INK).lineBetween(-20,0,-5,14).lineBetween(-5,14,23,-17);
  else if (kind === "close") g.lineBetween(-16,-16,16,16).lineBetween(-16,16,16,-16);
  else if (kind === "exit") {
    g.strokeRoundedRect(-24,-24,28,48,3);
    g.lineBetween(-8,0,28,0).lineBetween(28,0,16,-12).lineBetween(28,0,16,12);
  }
  else if (kind === "speed") {
    g.fillTriangle(-24,-18,-24,18,0,0).fillTriangle(0,-18,0,18,24,0);
  } else if (kind === "arrow") g.lineStyle(5,INK).lineBetween(0,-22,0,18).lineBetween(0,18,-14,4).lineBetween(0,18,14,4);
  else if (kind === "gift") {
    g.fillRoundedRect(-23,-4,46,29,4).fillRoundedRect(-26,-11,52,10,3);
    g.lineStyle(6,0x9b6946).lineBetween(0,-9,0,25);
    g.lineStyle(4,INK).strokeEllipse(-9,-18,19,14).strokeEllipse(9,-18,19,14);
  } else if (kind === "team") {
    g.fillCircle(0,-11,9).fillCircle(-20,-6,7).fillCircle(20,-6,7);
    g.fillRoundedRect(-11,2,22,22,10).fillRoundedRect(-29,6,16,15,6).fillRoundedRect(13,6,16,15,6);
  } else {
    g.fillStyle(0xf6d487).fillRoundedRect(-16,-22,32,30,8).fillRect(-4,6,8,13).fillRoundedRect(-15,18,30,7,3);
    g.lineStyle(5,0xf6d487).beginPath().arc(-15,-10,13,Math.PI/2,Math.PI*1.5).strokePath();
    g.beginPath().arc(15,-10,13,-Math.PI/2,Math.PI/2).strokePath();
  }
  return g;
}

function accessibleAction(scene: Phaser.Scene, label: string, action: () => void, focus: (value: boolean) => void): HTMLButtonElement {
  const control = document.createElement("button");
  control.className = "sr-only";
  control.type = "button";
  control.textContent = label;
  control.addEventListener("click", action);
  control.addEventListener("focus", () => focus(true));
  control.addEventListener("blur", () => focus(false));
  document.querySelector("#app-shell")?.appendChild(control);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => control.remove());
  return control;
}

export class PictureButton {
  readonly root: Phaser.GameObjects.Container;
  private readonly face: Phaser.GameObjects.Graphics;
  private readonly focusRing: Phaser.GameObjects.Graphics;
  private readonly hit: Phaser.GameObjects.Rectangle;
  private readonly accessible: HTMLButtonElement;
  private glyph!: Phaser.GameObjects.Graphics;
  private enabled = true;
  private active = false;
  private pressed = false;
  constructor(private scene: Phaser.Scene, x: number, y: number, private kind: Icon, label: string,
    private action: () => void, private size = 72) {
    const shadow = scene.add.graphics().fillStyle(0x122c2b,0.4).fillRoundedRect(-size/2, -size/2+6,size,size,16);
    this.face = scene.add.graphics();
    this.focusRing = scene.add.graphics().lineStyle(4,0xffe4a1).strokeRoundedRect(-size/2-5,-size/2-5,size+10,size+10,20).setVisible(false);
    this.hit = scene.add.rectangle(0,0,size,size,0,0).setInteractive({useHandCursor:true});
    this.root = scene.add.container(stageX(x),stageY(y),[shadow,this.face,this.focusRing,this.hit]).setDepth(650);
    this.draw(); this.setIcon(kind);
    this.accessible = accessibleAction(scene,label,()=>this.activate(),(value)=>this.focusRing.setVisible(value));
    this.hit.on("pointerover",()=>{if(this.enabled){this.focusRing.setVisible(true);this.scaleTo(1.06,150);}});
    this.hit.on("pointerout",()=>{this.focusRing.setVisible(false);if(!this.pressed)this.scaleTo(1,150);});
    this.hit.on("pointerdown",()=>this.activate());
  }
  private draw(): void {
    const s=this.size;
    this.face.clear().fillStyle(this.active ? 0x41765e : 0x29473e).fillRoundedRect(-s/2,-s/2,s,s,16);
    this.face.lineStyle(3,0xb9a57c).strokeRoundedRect(-s/2,-s/2,s,s,16);
    this.face.lineStyle(1,0xf5dfab,0.28).strokeRoundedRect(-s/2+5,-s/2+5,s-10,s-10,12);
  }
  private scaleTo(scale: number, duration: number): void {
    this.scene.tweens.killTweensOf(this.root);
    this.scene.tweens.add({targets:this.root,scale,duration:motionEnabled()?duration:0,ease:"Back.Out"});
  }
  private activate(): void {
    if(!this.enabled||this.pressed)return;
    this.pressed=true;
    this.scene.tweens.killTweensOf(this.root);
    this.root.setScale(0.92);
    this.scene.tweens.add({targets:this.root,scale:1,duration:motionEnabled()?180:0,ease:"Back.Out",
      onComplete:()=>{this.pressed=false;}});
    this.action();
  }
  setEnabled(value: boolean): void {
    this.enabled=value; this.accessible.disabled=!value;
    this.root.setAlpha(value?1:0.6);
    if(value)this.hit.setInteractive({useHandCursor:true});else this.hit.disableInteractive();
  }
  setLabel(label: string): void {this.accessible.textContent=label;}
  setActive(value: boolean): void {this.active=value;this.draw();}
  setIcon(kind: Icon): void {
    this.kind=kind;this.glyph?.destroy();this.glyph=icon(this.scene,this.kind,this.size*0.58);this.root.add(this.glyph);
  }
}

export class PortraitCard {
  readonly root: Phaser.GameObjects.Container;
  readonly portrait: Phaser.GameObjects.Image;
  readonly hit: Phaser.GameObjects.Rectangle;
  private readonly focusRing: Phaser.GameObjects.Graphics;
  private readonly accessible: HTMLButtonElement;
  private enabled = true;
  private floating = true;
  private readonly phase: number;
  private readonly foot: number;
  constructor(private scene: Phaser.Scene, x: number,y: number, public piece: Pick<Piece,"element"|"tier">,
    action:()=>void,width=208,height=216,delay=0) {
    const palette=COLORS[piece.element];
    const shadow=scene.add.graphics().fillStyle(0x152c2a,0.45).fillRoundedRect(-width/2,-height/2+8,width,height,25);
    const frame=scene.add.graphics().fillStyle(0x415348).fillRoundedRect(-width/2,-height/2,width,height,25);
    frame.lineStyle(4,0xbfac7e).strokeRoundedRect(-width/2,-height/2,width,height,25);
    frame.fillStyle(0xe9d7ad).fillRoundedRect(-width/2+9,-height/2+9,width-18,height-18,18);
    frame.lineStyle(2,0xfff0c5,0.65).strokeRoundedRect(-width/2+13,-height/2+13,width-26,height-26,15);
    const halo=scene.add.ellipse(0,0,width*0.76,height*0.58,palette.body,0.11);
    const ground=scene.add.ellipse(0,height*0.28,width*0.48,14,0x7c7054,0.2);
    this.foot=height*0.27;
    const occupancy=piece.tier<3?0.38:piece.tier<5?0.62:0.84;
    const size=height*0.64/occupancy;
    this.portrait=scene.add.image(0,this.foot,characterTexture(piece)).setOrigin(0.5,0.9).setDisplaySize(size,size);
    const rank=rankPips(scene,piece.tier,0,height/2-24,4.5);
    this.focusRing=scene.add.graphics().lineStyle(5,0xffe7a1).strokeRoundedRect(-width/2-5,-height/2-5,width+10,height+10,29).setVisible(false);
    this.hit=scene.add.rectangle(0,0,width,height,0,0).setInteractive({useHandCursor:true});
    this.root=scene.add.container(stageX(x),stageY(y),[shadow,frame,halo,ground,this.portrait,rank,this.focusRing,this.hit]).setDepth(630);
    this.phase=x*0.02;
    const activate=()=>{
      if(!this.enabled)return;
      this.floating=false;this.disable();action();
    };
    this.accessible=accessibleAction(scene,`Choose ${piece.element}, tier ${piece.tier}`,activate,value=>this.focusRing.setVisible(value));
    const hover=(value:boolean)=>{
      if(!this.enabled)return;
      this.focusRing.setVisible(value);
      scene.tweens.killTweensOf(this.root);
      // Hover can interrupt the entrance tween; finish its opacity as well as its scale.
      this.root.setAlpha(1);
      scene.tweens.add({targets:this.root,scale:value?1.045:1,duration:motionEnabled()?180:0,ease:"Back.Out"});
    };
    this.hit.on("pointerover",()=>hover(true)).on("pointerout",()=>hover(false)).on("pointerdown",activate);
    if(motionEnabled()) {
      this.root.setScale(0.72).setAlpha(0);
      scene.tweens.add({targets:this.root,scale:1,alpha:1,duration:400,delay,ease:"Back.Out"});
    }
  }
  update(time: number): void {
    if(!this.floating||!motionEnabled())return;
    this.portrait.y=this.foot+Math.sin(time/540+this.phase)*3;
  }
  setEnabled(value: boolean): void {
    this.enabled=value;this.accessible.disabled=!value;
    if(value)this.hit.setInteractive({useHandCursor:true});else this.hit.disableInteractive();
    if(!value)this.focusRing.setVisible(false);
  }
  disable(): void {this.setEnabled(false);}
  destroy(): void {
    this.scene.tweens.killTweensOf(this.root);
    this.accessible.remove();this.root.destroy();
  }
  focusPoint():{x:number;y:number} {
    const occupancy=this.piece.tier<3?0.38:this.piece.tier<5?0.62:0.84;
    return {x:this.root.x,y:this.root.y+this.portrait.y-this.portrait.displayHeight*occupancy/2};
  }
}

const DICE = [
  [[0,0]],
  [[-1,-1],[1,1]],
  [[-1,-1],[0,0],[1,1]],
  [[-1,-1],[1,-1],[-1,1],[1,1]],
  [[-1,-1],[1,-1],[0,0],[-1,1],[1,1]],
  [[-1,-1],[1,-1],[-1,0],[1,0],[-1,1],[1,1]],
];
export function rankPips(scene: Phaser.Scene,tier:number,x=0,y=0,radius=2.4):Phaser.GameObjects.Container {
  const root=scene.add.container(x,y), gold=tier>=6;
  // Keep practical ranks countable; enormous saved ranks still show their real value.
  if(tier>18) {
    root.add(scene.add.text(0,0,`${tier}`,{fontFamily:"Georgia, serif",fontSize:`${radius*7}px`,
      color:"#ffd573",stroke:"#594c36",strokeThickness:1}).setOrigin(0.5));
    return root;
  }
  const g=scene.add.graphics(), spacing=radius*2.8, groups=Math.ceil(tier/6);
  root.add(g);
  // Seven is a six-dot die plus one dot, eight is six plus two, and so on.
  for(let i=0;i<groups;i++) {
    const center=(i-(groups-1)/2)*radius*9.6;
    const value=Math.min(6,tier-i*6);
    for(const [px,py] of DICE[value-1]) {
      const dotX=center+px*spacing, dotY=py*spacing;
      g.fillStyle(gold?0xffd573:0xffefd0).fillCircle(dotX,dotY,radius);
      g.lineStyle(radius*0.28,0x594c36,0.65).strokeCircle(dotX,dotY,radius);
    }
  }
  return root;
}

export function teamDock(scene: Phaser.Scene, board: Board): Phaser.GameObjects.Container {
  const w = 352, h = 122;
  const frame = scene.add.graphics();
  frame.fillStyle(0x213b34, 0.9).fillRoundedRect(-w/2, -h/2, w, h, 24);
  frame.lineStyle(2, 0xb9a57c, 0.8).strokeRoundedRect(-w/2, -h/2, w, h, 24);
  const root = scene.add.container(stageX(226), stageY(923), [frame]).setDepth(600);
  (["fire", "ice", "lightning"] as const).forEach((element, i) => {
    const count = board.cells.filter(p => p?.element === element).length;
    root.add(scene.add.image((i-1)*106, 5, characterTexture({element, tier:1})).setOrigin(0.5, 0.9)
      .setDisplaySize(145, 145).setAlpha(count ? 1 : 0.3));
    root.add(scene.add.text((i-1)*106, 30, `${count}`, {fontFamily:"Georgia, serif", fontSize:"23px", color:"#fff0cf"}).setOrigin(0.5));
  });
  return root;
}

export class BattleButton {
  readonly root:Phaser.GameObjects.Container;
  private readonly hit:Phaser.GameObjects.Rectangle;
  private readonly accessible:HTMLButtonElement;
  private enabled=true;
  constructor(private scene:Phaser.Scene, x:number,y:number,action:()=>void,label="Battle", kind?:Icon) {
    const shadow=scene.add.ellipse(0,48,275,32,0x112c2a,0.35);
    const frame=scene.add.image(0,0,UI_TEXTURES.battleFrame).setDisplaySize(336,112);
    const emblem=kind ? icon(scene,kind,66).setPosition(0,-4) : scene.add.image(0,-8,UI_TEXTURES.battleEmblem).setDisplaySize(122,122);
    const glow=scene.add.graphics().lineStyle(3,0xffe7ab,0.7).strokeRoundedRect(-158,-48,316,96,20).setVisible(false);
    this.hit=scene.add.rectangle(0,0,330,112,0,0).setInteractive({useHandCursor:true});
    this.root=scene.add.container(stageX(x),stageY(y),[shadow,frame,emblem,glow,this.hit]).setDepth(640);
    const activate=()=>{if(!this.enabled)return;action();};
    this.accessible=accessibleAction(scene,label,activate,value=>glow.setVisible(value));
    this.hit.on("pointerover",()=>{if(this.enabled){glow.setVisible(true);this.scaleTo(1.045);}})
      .on("pointerout",()=>{glow.setVisible(false);this.scaleTo(1);}).on("pointerdown",activate);
  }
  private scaleTo(scale:number):void {
    this.scene.tweens.killTweensOf(this.root);
    this.scene.tweens.add({targets:this.root,scale,duration:motionEnabled()?180:0,ease:"Back.Out"});
  }
  setEnabled(value:boolean):void {
    this.enabled=value;this.hit[value?"setInteractive":"disableInteractive"]();
    if(this.accessible)this.accessible.disabled=!value;
    this.root.setAlpha(value?1:0.5);
  }
}
